import axios from 'axios';
import {
  Category,
  Product,
  Order,
  Content,
  CategoryCreate,
  ProductCreate,
  OrderCreate,
  OrderEditRequest,
  UserLogin,
  LoginRequest,
  LoginResponse,
  ContentUpdate,
  OrderAnalytics,
  StockValidationRequest,
  StockValidationResponse
} from '../types';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — clear stale token and redirect to admin login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAdminRoute = window.location.pathname.startsWith('/adddmin');
      if (isAdminRoute) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/adddmin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/admin/login', credentials);
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },
  
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('admin_token');
  },
  
  getToken: (): string | null => {
    return localStorage.getItem('admin_token');
  }
};

// Categories API
export const categoriesAPI = {
  getAll: async (): Promise<Category[]> => {
    const response = await api.get('/categories');
    // Sort by order field, then by creation date
    return response.data.sort((a: Category, b: Category) => 
      (a.order || 0) - (b.order || 0) || 
      new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime()
    );
  },
  
  create: async (category: CategoryCreate): Promise<Category> => {
    const response = await api.post('/admin/categories', category);
    return response.data;
  },
  
  reorder: async (items: {id: string; order: number}[]): Promise<void> => {
    await api.put('/admin/categories/reorder', { items });
  },
  
  update: async (id: string, category: Partial<CategoryCreate>): Promise<Category> => {
    const response = await api.put(`/admin/categories/${id}`, category);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/categories/${id}`);
  },
  
  uploadImage: async (id: string, file: File): Promise<{url: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/categories/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// Products API
export const productsAPI = {
  getAll: async (categoryId?: string, search?: string): Promise<Product[]> => {
    const params = new URLSearchParams();
    if (categoryId) params.append('category_id', categoryId);
    if (search) params.append('search', search);
    
    const queryString = params.toString();
    const url = queryString ? `/products?${queryString}` : '/products';
    const response = await api.get(url);
    // Sort by order field, then by creation date
    return response.data.sort((a: Product, b: Product) => 
      (a.order || 0) - (b.order || 0) || 
      new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime()
    );
  },
  
  getById: async (id: string): Promise<Product> => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  
  create: async (product: ProductCreate): Promise<Product> => {
    const response = await api.post('/admin/products', product);
    return response.data;
  },
  
  reorder: async (items: {id: string; order: number}[]): Promise<void> => {
    await api.put('/admin/products/reorder', { items });
  },
  
  update: async (id: string, product: Partial<ProductCreate>): Promise<Product> => {
    const response = await api.put(`/admin/products/${id}`, product);
    return response.data;
  },
  
  toggleBestSeller: async (id: string, bestSeller: boolean): Promise<Product> => {
    const response = await api.patch(`/admin/products/${id}/best-seller?best_seller=${bestSeller}`);
    return response.data;
  },
  
  toggleNewlyLaunched: async (id: string, newlyLaunched: boolean): Promise<Product> => {
    const response = await api.patch(`/admin/products/${id}/newly-launched?newly_launched=${newlyLaunched}`);
    return response.data;
  },
  
  toggleThisWeeksFresh: async (id: string, thisWeeksFresh: boolean): Promise<Product> => {
    const response = await api.patch(`/admin/products/${id}/this-weeks-fresh?this_weeks_fresh=${thisWeeksFresh}`);
    return response.data;
  },
  
  getFeatured: async (): Promise<{ newly_launched: Product | null; this_weeks_fresh: Product | null }> => {
    const response = await api.get('/products/featured');
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/products/${id}`);
  },
  
  uploadImage: async (id: string, file: File): Promise<{url: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/products/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// Stock validation API
export const stockAPI = {
  validateStock: async (request: StockValidationRequest): Promise<StockValidationResponse> => {
    const response = await api.post('/validate-stock', request);
    return response.data;
  }
};

// Orders API
export const ordersAPI = {
  create: async (order: OrderCreate): Promise<Order> => {
    const response = await api.post('/orders', order);
    return response.data;
  },
  
  getUserOrders: async (email: string, password: string): Promise<Order[]> => {
    const response = await api.post('/orders/user', { email, password });
    return response.data;
  },
  
  getAll: async (): Promise<Order[]> => {
    const response = await api.get('/admin/orders');
    return response.data;
  },
  
  updateStatus: async (id: string, status: string): Promise<Order> => {
    const response = await api.put(`/admin/orders/${id}/status`, { status });
    return response.data;
  },
  
  editOrder: async (id: string, orderEdit: OrderEditRequest, userLogin: UserLogin): Promise<Order> => {
    const response = await api.put(`/orders/${id}/edit`, { ...orderEdit, ...userLogin });
    return response.data;
  },
  
  adminEditOrder: async (id: string, orderEdit: OrderEditRequest): Promise<Order> => {
    const response = await api.put(`/admin/orders/${id}/edit`, orderEdit);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/orders/${id}`);
  },
  
  getAnalytics: async (
    startDate?: string, 
    endDate?: string, 
    groupBy?: 'product' | 'week' | 'month'
  ): Promise<OrderAnalytics[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (groupBy) params.append('group_by', groupBy);
    
    const queryString = params.toString();
    const url = queryString ? `/admin/orders/analytics?${queryString}` : '/admin/orders/analytics';
    
    const response = await api.get(url);
    return response.data;
  },

  getSummary: async (
    startDate?: string, 
    endDate?: string
  ): Promise<{
    total_orders: number;
    total_revenue: number;
    avg_order_value: number;
    total_items_sold: number;
    min_order_value: number;
    max_order_value: number;
    status_counts: Record<string, number>;
  }> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString();
    const url = queryString ? `/admin/orders/summary?${queryString}` : '/admin/orders/summary';
    
    const response = await api.get(url);
    return response.data;
  }
};

// Subscriptions API
export const subscriptionsAPI = {
  adminGetAll: async () => {
    const response = await api.get('/admin/subscriptions');
    return response.data;
  },
  create: async (data: {
    email: string; password: string;
    user_name: string; user_phone: string; user_address: string;
    items: { product_id: string; product_name: string; quantity: number; price: number }[];
    day_of_week: number; notes?: string;
  }) => {
    const response = await api.post('/subscriptions', data);
    return response.data;
  },

  getMy: async (email: string, password: string) => {
    const response = await api.post('/subscriptions/my', { email, password });
    return response.data;
  },

  update: async (
    id: string,
    updates: Partial<{ user_name: string; user_phone: string; user_address: string; day_of_week: number; active: boolean; notes: string; items: any[] }>,
    email: string,
    password: string
  ) => {
    const response = await api.put(`/subscriptions/${id}`, { ...updates, email, password });
    return response.data;
  },

  cancel: async (id: string, email: string, password: string) => {
    const response = await api.delete(`/subscriptions/${id}`, { data: { email, password } });
    return response.data;
  },
};

// User API
export const userAPI = {
  login: async (credentials: UserLogin): Promise<{ message: string; user_id: string }> => {
    const response = await api.post('/user/login', credentials);
    return response.data;
  }
};

// Content API
export const contentAPI = {
  get: async (page: string): Promise<Content> => {
    const response = await api.get(`/content/${page}`);
    return response.data;
  },
  
  getAll: async (): Promise<Content[]> => {
    const response = await api.get('/content');
    return response.data;
  },
  
  getSection: async (page: string, section: string): Promise<Content> => {
    const response = await api.get(`/content/${page}/${section}`);
    return response.data;
  },
  
  create: async (content: {
    page: string;
    section: string;
    title: string;
    content: string;
    order?: number;
  }): Promise<Content> => {
    const response = await api.post('/admin/content', content);
    return response.data;
  },
  
  update: async (page: string, content: ContentUpdate): Promise<Content> => {
    const response = await api.put(`/admin/content/${page}`, content);
    return response.data;
  },
  
  updateById: async (id: string, content: ContentUpdate): Promise<Content> => {
    const response = await api.put(`/admin/content/id/${id}`, content);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/content/${id}`);
  },
  
  uploadLogo: async (page: string, file: File): Promise<{url: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/content/${page}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// Recipes API
export const recipesAPI = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/recipes');
    return response.data;
  },
  
  create: async (recipe: any): Promise<any> => {
    const response = await api.post('/admin/recipes', recipe);
    return response.data;
  },
  
  update: async (id: string, recipe: any): Promise<any> => {
    const response = await api.put(`/admin/recipes/${id}`, recipe);
    return response.data;
  },
  
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/recipes/${id}`);
  },
  
  uploadImage: async (id: string, file: File): Promise<{url: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/recipes/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  uploadPdf: async (id: string, file: File): Promise<{url: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/admin/recipes/${id}/pdf`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// Contact Info API
export const contactAPI = {
  get: async (): Promise<{
    _id?: string;
    company_name: string;
    company_description: string;
    email: string;
    phone: string;
    address: string;
    updated_at?: string;
  }> => {
    const response = await api.get('/contact-info');
    return response.data;
  },
  
  update: async (contactInfo: {
    company_name?: string;
    company_description?: string;
    email?: string;
    phone?: string;
    address?: string;
  }): Promise<{
    _id?: string;
    company_name: string;
    company_description: string;
    email: string;
    phone: string;
    address: string;
    updated_at?: string;
  }> => {
    const response = await api.put('/admin/contact-info', contactInfo);
    return response.data;
  }
};

// File Upload API
export const uploadAPI = {
  uploadFile: async (file: File): Promise<{filename: string, url: string}> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/admin/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// System Settings API
export const systemSettingsAPI = {
  get: async (key: string): Promise<any> => {
    const response = await api.get(`/settings/${key}`);
    return response.data;
  },
  
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/admin/settings');
    return response.data;
  },
  
  update: async (key: string, value: number, description?: string): Promise<any> => {
    const response = await api.put(`/admin/settings/${key}`, { 
      value, 
      description 
    });
    return response.data;
  }
};

export default api;
